import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money, openImpersonation } from "@/lib/api";
import { can, canImpersonate, isPlatformOperator } from "@/lib/permissions";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UsersThree, Plus, SlidersHorizontal, SignIn, Prohibit, Play, Trash, ArrowLeft, ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import CreateWizardSteps from "@/components/CreateWizardSteps";
import AdjustBalanceModal from "@/components/AdjustBalanceModal";

const emptyForm = {
  name: "", company: "", email: "", password: "", reseller_id: "__direct__",
  contact_phone: "", call_limit_price: "", call_limit_pricing: "standard",
  opensips_server_ids: [], media_server_ids: [], ai_engine_ids: [],
};

const CUSTOMER_STEPS = ["Details", "Engines", "Pricing"];

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = isPlatformOperator(user);
  const isFullAdmin = user?.role === "admin";
  const isReseller = user?.role === "reseller";
  const canAdd = can(user, "customers", "add") || isReseller;
  const canEdit = can(user, "customers", "edit") || isReseller;
  const canDelete = can(user, "customers", "delete") || isReseller;
  const [rows, setRows] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [opensips, setOpensips] = useState([]);
  const [media, setMedia] = useState([]);
  const [engines, setEngines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("direct");
  const [statusFilter, setStatusFilter] = useState("all");
  const [resellerFilter, setResellerFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [assignees, setAssignees] = useState([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [manage, setManage] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [stdPrices, setStdPrices] = useState(null);
  const [balanceTarget, setBalanceTarget] = useState(null);

  useEffect(() => {
    if (!open) { setStep(0); return; }
    // Admin: platform customer defaults. Reseller: their customer catalog (or platform fallback).
    const priceReq = isReseller
      ? api.get("/billing/customer-price").then(({ data }) => ({
          customer_price_minor: data?.standard_customer_price_minor,
          customer_ai_model_price_minor: data?.standard_customer_ai_model_price_minor,
          customer_tts_price_minor: data?.standard_customer_tts_price_minor,
          customer_stt_price_minor: data?.standard_customer_stt_price_minor,
        }))
      : api.get("/billing/pricing").then(({ data }) => data || null);
    priceReq.then((data) => setStdPrices(data || null)).catch(() => setStdPrices(null));
    // Pre-select platform default engines when opening create wizard
    setForm((f) => {
      if (f.opensips_server_ids.length || f.media_server_ids.length || f.ai_engine_ids.length) return f;
      return {
        ...f,
        opensips_server_ids: opensips.filter((s) => s.is_default).map((s) => s.id),
        media_server_ids: media.filter((s) => s.is_default).map((s) => s.id),
        ai_engine_ids: engines.filter((s) => s.is_default && s.enabled !== false && s.status !== "DISABLED").map((s) => s.id),
      };
    });
  }, [open, opensips, media, engines, isReseller]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${delTarget.id}`);
      toast.success("Customer deleted");
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAdmin) {
        if (typeFilter === "direct") params.only_direct = true;
        else if (typeFilter === "reseller") {
          if (resellerFilter !== "all") params.reseller_id = resellerFilter;
          else params.only_reseller = true;
        } else if (resellerFilter !== "all") {
          params.reseller_id = resellerFilter;
        }
        if (statusFilter !== "all") params.status = statusFilter;
      } else if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      if (searchQ.trim()) params.q = searchQ.trim();
      if (isFullAdmin && assignedFilter && assignedFilter !== "all") params.assigned_to = assignedFilter;
      const { data: customerRows } = await api.get("/customers", { params });
      setRows(customerRows || []);
      if (isAdmin) {
        const settled = await Promise.allSettled([
          api.get("/resellers"),
          api.get("/servers/opensips"),
          api.get("/servers/media"),
          api.get("/ai-engines"),
          isFullAdmin ? api.get("/admin-users/assignees") : Promise.resolve({ data: [] }),
        ]);
        const val = (i) => (settled[i].status === "fulfilled" ? settled[i].value.data : null);
        setResellers(val(0) || []);
        setOpensips(val(1) || []);
        setMedia(val(2) || []);
        setEngines(val(3) || []);
        setAssignees(val(4) || []);
      } else if (isReseller) {
        const { data: s } = await api.get("/resellers/me/servers");
        setOpensips(s?.opensips_servers || []); setMedia(s?.media_servers || []); setEngines(s?.ai_engine_servers || []);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isFullAdmin, isReseller, typeFilter, statusFilter, resellerFilter, searchQ, assignedFilter]);

  useEffect(() => { load(); }, [load]);

  // Debounce search typing before hitting the API.
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const canNextDetails = form.name.trim() && form.company.trim() && form.email.trim() && form.password.length >= 8;

  const nextStep = () => {
    if (step === 0 && !canNextDetails) {
      toast.error(form.password && form.password.length < 8
        ? "Password must be at least 8 characters"
        : "Fill in contact name, company, email, and password.");
      return;
    }
    setStep((s) => Math.min(CUSTOMER_STEPS.length - 1, s + 1));
  };

  const create = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name, company: form.company, email: form.email,
        password: form.password, contact_phone: form.contact_phone,
      };
      if (isAdmin) {
        body.reseller_id = form.reseller_id === "__direct__" ? null : form.reseller_id;
        body.pricing_source = body.reseller_id ? "reseller" : "platform";
      }
      body.call_limit_pricing = form.call_limit_pricing || "standard";
      if (form.call_limit_pricing === "custom") {
        body.call_limit_price_minor = Math.max(0, Math.round(parseFloat(form.call_limit_price || 0) * 100));
      }
      if (isAdmin || isReseller) {
        body.opensips_server_ids = form.opensips_server_ids;
        body.media_server_ids = form.media_server_ids;
        body.ai_engine_ids = form.ai_engine_ids;
      }
      await api.post("/customers", body);
      toast.success("Customer created");
      setOpen(false); setForm(emptyForm); setStep(0); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "suspended" : "active";
    try { await api.patch(`/customers/${c.id}/status`, { status: next }); toast.success(`Customer ${next}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle={isAdmin ? "Direct customers by default — use filters to change the view" : "Your customers"}>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setStep(0); setForm(emptyForm); } }}>
          {canAdd && (
          <DialogTrigger asChild>
            <Button data-testid="create-customer-btn" className="rounded-lg">
              <Plus className="w-4 h-4 mr-1.5" weight="bold" /> New Customer
            </Button>
          </DialogTrigger>
          )}
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="create-customer-dialog">
            <DialogHeader>
              <DialogTitle>Create customer</DialogTitle>
              <DialogDescription>Step {step + 1} of {CUSTOMER_STEPS.length}: {CUSTOMER_STEPS[step]}</DialogDescription>
            </DialogHeader>
            <CreateWizardSteps steps={CUSTOMER_STEPS} current={step} onSelect={setStep} testid="customer-wizard-steps" />

            {step === 0 && (
              <div className="space-y-4 py-1" data-testid="customer-wizard-details">
                {isAdmin && (
                  <div className="space-y-2">
                    <Label>Ownership</Label>
                    <Select value={form.reseller_id} onValueChange={(v) => setForm({ ...form, reseller_id: v })}>
                      <SelectTrigger data-testid="customer-owner-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__direct__">Direct Customer (managed by Admin)</SelectItem>
                        {resellers.map((r) => <SelectItem key={r.id} value={r.id}>Reseller: {r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Contact name</Label>
                    <Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Company</Label>
                    <Input data-testid="customer-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Email</Label>
                  <Input data-testid="customer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="space-y-2"><Label>Password</Label>
                  <Input data-testid="customer-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" /></div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4 py-1" data-testid="customer-wizard-engines">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{isReseller ? "Assign Telephony" : "Assign OpenSIPS"}</Label>
                    <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto">
                      {opensips.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm">
                          <Checkbox data-testid={`opensips-${s.id}`} checked={form.opensips_server_ids.includes(s.id)}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, opensips_server_ids: v ? [...f.opensips_server_ids, s.id] : f.opensips_server_ids.filter((x) => x !== s.id) }))} />
                          {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                        </label>
                      ))}
                      {opensips.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Assign Media</Label>
                    <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto">
                      {media.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm">
                          <Checkbox data-testid={`media-${s.id}`} checked={form.media_server_ids.includes(s.id)}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, media_server_ids: v ? [...f.media_server_ids, s.id] : f.media_server_ids.filter((x) => x !== s.id) }))} />
                          {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                        </label>
                      ))}
                      {media.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Assign AI Engine servers <span className="text-muted-foreground text-xs font-normal">(leave empty = any healthy engine)</span></Label>
                  <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto" data-testid="create-aiengine-list">
                    {engines.filter((s) => s.enabled !== false && s.status !== "DISABLED").map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm">
                        <Checkbox data-testid={`aiengine-${s.id}`} checked={form.ai_engine_ids.includes(s.id)}
                          onCheckedChange={(v) => setForm((f) => ({ ...f, ai_engine_ids: v ? [...f.ai_engine_ids, s.id] : f.ai_engine_ids.filter((x) => x !== s.id) }))} />
                        {s.name} <span className="text-muted-foreground text-xs">{s.public_ip} · {s.status}</span>
                      </label>
                    ))}
                    {engines.filter((s) => s.enabled !== false && s.status !== "DISABLED").length === 0 && (
                      <p className="text-xs text-muted-foreground">No active AI engines available.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 py-1" data-testid="customer-wizard-pricing">
                <div className="space-y-2">
                  <Label>Call-limit pricing</Label>
                  <Select value={form.call_limit_pricing} onValueChange={(v) => setForm({ ...form, call_limit_pricing: v })}>
                    <SelectTrigger data-testid="customer-pricing-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">
                        {isReseller ? "Standard (your customer price)" : "Standard (admin customer price)"}
                      </SelectItem>
                      <SelectItem value="custom">Custom channel price</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.call_limit_pricing === "custom" && (
                    <Input data-testid="customer-pricing-custom" type="number" min="0" step="0.01"
                      value={form.call_limit_price}
                      onChange={(e) => setForm({ ...form, call_limit_price: e.target.value })}
                      placeholder="USD per concurrent channel" />
                  )}
                </div>
                <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prices that will be assigned</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Call limit / channel</p>
                      <p className="font-semibold tabular-nums">
                        {form.call_limit_pricing === "custom" && form.call_limit_price !== ""
                          ? money(Math.round(parseFloat(form.call_limit_price || 0) * 100))
                          : (stdPrices?.customer_price_minor != null ? money(stdPrices.customer_price_minor) : "Not set")}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">AI Model / min</p>
                      <p className="font-semibold tabular-nums">
                        {stdPrices?.customer_ai_model_price_minor != null ? money(stdPrices.customer_ai_model_price_minor) : "Not set"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">TTS / min</p>
                      <p className="font-semibold tabular-nums">
                        {stdPrices?.customer_tts_price_minor != null ? money(stdPrices.customer_tts_price_minor) : "Not set"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">STT / min</p>
                      <p className="font-semibold tabular-nums">
                        {stdPrices?.customer_stt_price_minor != null ? money(stdPrices.customer_stt_price_minor) : "Not set"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              {step > 0 && (
                <Button type="button" variant="outline" className="rounded-lg" onClick={() => setStep((s) => s - 1)} data-testid="customer-wizard-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              {step < CUSTOMER_STEPS.length - 1 ? (
                <Button type="button" className="rounded-lg" onClick={nextStep} data-testid="customer-wizard-next">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={create} disabled={saving} data-testid="submit-customer-btn" className="rounded-lg">
                  {saving ? "Creating…" : "Create customer"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="customer-filters">
        <div className="space-y-1.5 flex-1 min-w-[200px] max-w-sm">
          <Label className="text-xs text-muted-foreground">Search</Label>
          <div className="relative">
            <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              data-testid="customer-search"
              className="h-9 rounded-lg pl-8"
              placeholder="Company, email, name, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        {isAdmin && (
          <>
            <div className="space-y-1.5 min-w-[140px]">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={typeFilter} onValueChange={(v) => {
                setTypeFilter(v);
                if (v === "direct") setResellerFilter("all");
              }}>
                <SelectTrigger data-testid="filter-type" className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct" data-testid="filter-direct">Direct</SelectItem>
                  <SelectItem value="reseller" data-testid="filter-reseller">Via Reseller</SelectItem>
                  <SelectItem value="all" data-testid="filter-all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(typeFilter === "reseller" || typeFilter === "all") && (
              <div className="space-y-1.5 min-w-[180px]">
                <Label className="text-xs text-muted-foreground">Reseller</Label>
                <Select value={resellerFilter} onValueChange={setResellerFilter}>
                  <SelectTrigger data-testid="filter-reseller-select" className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All resellers</SelectItem>
                    {resellers.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isFullAdmin && (
            <div className="space-y-1.5 min-w-[180px]">
              <Label className="text-xs text-muted-foreground">Assigned to</Label>
              <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                <SelectTrigger data-testid="filter-assigned-to" className="h-9 rounded-lg"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {assignees.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name || a.email}{a.role === "staff" ? " (staff)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
          </>
        )}
        <div className="space-y-1.5 min-w-[140px]">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="customers-empty" icon={UsersThree}
            title={searchQ.trim()
              ? "No customers match your search"
              : typeFilter === "direct" && isAdmin
                ? "No direct customers"
                : "No customers match"}
            description={searchQ.trim()
              ? "Try a different email, company name, or clear search."
              : isAdmin
                ? "Try another filter, or create a new customer."
                : "Add your first customer to get started."} />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="customers-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Type</TableHead>
                  {isAdmin && <TableHead>Reseller</TableHead>}
                  {isAdmin && <TableHead>Assigned to</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Call-limit</TableHead>
                  <TableHead>Signup date</TableHead>
                  <TableHead className="text-center">Campaigns</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} className="hover:bg-muted/40" data-testid={`customer-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      {(c.email || c.contact_email) && (
                        <div className="text-xs text-muted-foreground truncate max-w-[220px]" title={c.email || c.contact_email}>
                          {c.email || c.contact_email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${c.customer_type === "direct" ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground border-border"}`}>
                        {c.customer_type || (c.reseller_id ? "reseller" : "direct")}
                      </span>
                    </TableCell>
                    {isAdmin && <TableCell className="text-sm text-muted-foreground">{c.reseller_name || "—"}</TableCell>}
                    {isAdmin && (
                      <TableCell className="text-sm text-muted-foreground" data-testid={`customer-assigned-${c.id}`}>
                        {c.assigned_to_name || "—"}
                      </TableCell>
                    )}
                    <TableCell><StatusPill status={c.status} /></TableCell>
                    <TableCell className="text-right tabular-nums">
                      <button
                        type="button"
                        className="font-semibold text-primary hover:underline underline-offset-2"
                        onClick={() => setBalanceTarget(c)}
                        data-testid={`customer-balance-${c.id}`}
                        title="Adjust balance"
                      >
                        {money(c.cash_balance_minor || 0)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right tabular-nums" data-testid={`customer-calllimit-${c.id}`}>{c.concurrent_call_limit != null ? c.concurrent_call_limit : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-testid={`customer-signup-${c.id}`}>{c.created_at ? new Date(c.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}</TableCell>
                    <TableCell className="text-center tabular-nums">{c.campaign_count}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center justify-end gap-1">
                        {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg h-8 w-8 p-0"
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/customers/${c.id}`);
                              setManage(data);
                            } catch (e) {
                              toast.error(formatApiError(e.response?.data?.detail, e));
                              setManage(c);
                            }
                          }}
                          data-testid={`manage-customer-${c.id}`}
                          title="Manage"
                          aria-label="Manage"
                        >
                          <SlidersHorizontal className="w-4 h-4" />
                        </Button>
                        )}
                        {canImpersonate(user) && (
                        <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0" data-testid={`login-as-customer-${c.id}`} title="Login as" aria-label="Login as"
                          onClick={async () => { try { await openImpersonation(c.id); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>
                          <SignIn className="w-4 h-4" />
                        </Button>
                        )}
                        {canEdit && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleStatus(c)} data-testid={`toggle-customer-${c.id}`}
                          title={c.status === "active" ? "Suspend" : "Activate"} aria-label={c.status === "active" ? "Suspend" : "Activate"}>
                          {c.status === "active" ? <Prohibit className="w-4 h-4" /> : <Play className="w-4 h-4" weight="fill" />}
                        </Button>
                        )}
                        {canDelete && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => setDelTarget(c)} data-testid={`delete-customer-${c.id}`} title="Delete" aria-label="Delete">
                          <Trash className="w-4 h-4" />
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <ManageCustomer customer={manage} isAdmin={isAdmin} isReseller={isReseller} opensips={opensips} media={media} engines={engines}
        onClose={() => setManage(null)}
        onSaved={async () => {
          await load();
          if (manage?.id) {
            try {
              const { data } = await api.get(`/customers/${manage.id}`);
              setManage(data);
            } catch { /* keep modal open with prior row */ }
          }
        }} />

      <AdjustBalanceModal
        account={balanceTarget}
        open={!!balanceTarget}
        onClose={() => setBalanceTarget(null)}
        onSaved={() => load()}
      />

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent data-testid="delete-customer-dialog">
          <DialogHeader><DialogTitle>Delete {delTarget?.name}?</DialogTitle>
            <DialogDescription>
              This deactivates the customer and its login. Active campaigns must be stopped first. This cannot be undone from the UI.
            </DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDelTarget(null)} data-testid="cancel-delete-customer">Cancel</Button>
            <Button className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete} disabled={deleting} data-testid="confirm-delete-customer">
              {deleting ? "Deleting…" : "Delete customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManageCustomer({ customer, isAdmin, isReseller, opensips, media, engines, onClose, onSaved }) {
  const MANAGE_STEPS = isAdmin
    ? ["Details", "Engines", "Pricing", "AI"]
    : ["Details", "Engines", "Pricing"];
  const [step, setStep] = useState(0);
  const [ops, setOps] = useState([]);
  const [med, setMed] = useState([]);
  const [eng, setEng] = useState([]);
  const [busy, setBusy] = useState(false);
  const [acct, setAcct] = useState({ name: "", email: "", password: "", status: "active" });
  const [customPrice, setCustomPrice] = useState("");
  const [usagePrices, setUsagePrices] = useState({ ai: "", tts: "", stt: "" });
  const [callLimit, setCallLimit] = useState("");
  const [amdMode, setAmdMode] = useState("LAYER1_WITH_CONDITIONAL_LAYER2");
  const [catalogs, setCatalogs] = useState({ models: [], tts: [], stt: [] });
  const [aiForm, setAiForm] = useState({ ai_model_id: "__none__", tts_id: "", stt_id: "" });

  const dollars = (m) => (m != null ? (m / 100).toFixed(2) : "");

  useEffect(() => {
    if (!customer) {
      setStep(0);
      return;
    }
    setStep(0);
    setOps(customer.opensips_server_ids || []);
    setMed(customer.media_server_ids || []);
    setEng(customer.ai_engine_ids || []);
    setAcct({
      name: customer.name || "",
      email: customer.contact_email || "",
      password: "",
      status: customer.status || "active",
    });
    setCustomPrice(dollars(customer.call_limit_price_minor));
    setUsagePrices({
      ai: dollars(customer.ai_model_price_minor),
      tts: dollars(customer.tts_price_minor),
      stt: dollars(customer.stt_price_minor),
    });
    setCallLimit(customer.concurrent_call_limit != null ? String(customer.concurrent_call_limit) : "");
    setAmdMode(customer.amd_detection_mode || "LAYER1_WITH_CONDITIONAL_LAYER2");
  }, [customer]);

  useEffect(() => {
    if (!customer || !isAdmin) return;
    let cancelled = false;
    Promise.allSettled([
      api.get("/ai-bots/models"),
      api.get("/ai-bots/tts"),
      api.get("/ai-bots/stt"),
      api.get(`/ai-bots/customers/${customer.id}`),
    ]).then((results) => {
      if (cancelled) return;
      const ok = (i) => results[i].status === "fulfilled" ? results[i].value.data : null;
      const fail = results.find((r) => r.status === "rejected");
      setCatalogs({
        models: ok(0) || [],
        tts: ok(1) || [],
        stt: ok(2) || [],
      });
      const a = ok(3) || {};
      setAiForm({
        ai_model_id: a.ai_model_id || "__none__",
        tts_id: a.tts_id || "",
        stt_id: a.stt_id || "",
      });
      if (fail) {
        toast.error(formatApiError(fail.reason?.response?.data?.detail, fail.reason));
      }
    });
    return () => { cancelled = true; };
  }, [customer, isAdmin]);

  if (!customer) return null;

  const optionRows = (list, selectedId) =>
    (list || []).filter((r) => r.status === "active" || r.id === selectedId);

  const canNextDetails = acct.name.trim() && acct.email.trim()
    && (!acct.password || acct.password.length >= 8);

  const nextStep = () => {
    if (step === 0 && !canNextDetails) {
      toast.error(acct.password && acct.password.length < 8
        ? "Password must be at least 8 characters"
        : "Contact name and login email are required.");
      return;
    }
    if (isAdmin && step === MANAGE_STEPS.length - 2) {
      // Leaving Pricing → AI: no hard validation
    }
    setStep((s) => Math.min(MANAGE_STEPS.length - 1, s + 1));
  };

  const saveAll = async () => {
    if (acct.password && acct.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      setStep(0);
      return;
    }
    if (isAdmin && (!aiForm.tts_id || !aiForm.stt_id)) {
      toast.error("TTS and STT are required.");
      setStep(MANAGE_STEPS.indexOf("AI"));
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      if (acct.name && acct.name !== customer.name) payload.name = acct.name;
      if (acct.email && acct.email !== customer.contact_email) payload.email = acct.email;
      if (acct.password) payload.password = acct.password;
      if ((isAdmin || isReseller) && customPrice !== "") {
        payload.call_limit_pricing = "custom";
        payload.call_limit_price_minor = Math.max(0, Math.round(parseFloat(customPrice || 0) * 100));
      }
      if (isAdmin || isReseller) {
        const toMinor = (v) => (v === "" || v == null || Number.isNaN(parseFloat(v))
          ? null
          : Math.max(0, Math.round(parseFloat(v) * 100)));
        const ai = toMinor(usagePrices.ai);
        const tts = toMinor(usagePrices.tts);
        const stt = toMinor(usagePrices.stt);
        if (ai !== customer.ai_model_price_minor) payload.ai_model_price_minor = ai;
        if (tts !== customer.tts_price_minor) payload.tts_price_minor = tts;
        if (stt !== customer.stt_price_minor) payload.stt_price_minor = stt;
      }
      if ((isAdmin || isReseller) && callLimit !== "" && String(customer.concurrent_call_limit ?? "") !== callLimit) {
        payload.concurrent_call_limit = Math.max(0, parseInt(callLimit || "0", 10));
      }
      if ((isAdmin || isReseller) && amdMode !== (customer.amd_detection_mode || "LAYER1_WITH_CONDITIONAL_LAYER2")) {
        payload.amd_detection_mode = amdMode;
      }
      if (Object.keys(payload).length) await api.patch(`/customers/${customer.id}`, payload);
      if (acct.status !== customer.status) {
        await api.patch(`/customers/${customer.id}/status`, { status: acct.status });
      }
      if (isAdmin || isReseller) {
        await api.put(`/customers/${customer.id}/servers`, {
          opensips_server_ids: ops,
          media_server_ids: med,
          ai_engine_ids: eng,
        });
      }
      if (isAdmin) {
        await api.put(`/ai-bots/customers/${customer.id}`, {
          ai_model_id: aiForm.ai_model_id === "__none__" ? null : aiForm.ai_model_id,
          tts_id: aiForm.tts_id,
          stt_id: aiForm.stt_id,
        });
      }
      toast.success("Customer updated");
      setAcct((a) => ({ ...a, password: "" }));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  /** Save from any step — admin/reseller can finish after editing Details/Engines/etc. */
  const finishEarly = () => {
    if (!canNextDetails) {
      toast.error(acct.password && acct.password.length < 8
        ? "Password must be at least 8 characters"
        : "Contact name and login email are required.");
      setStep(0);
      return;
    }
    saveAll();
  };

  const last = step === MANAGE_STEPS.length - 1;

  return (
    <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="manage-customer-dialog">
        <DialogHeader>
          <DialogTitle>Edit customer · {customer.name}</DialogTitle>
          <DialogDescription>
            Step {step + 1} of {MANAGE_STEPS.length}: {MANAGE_STEPS[step]}
            {customer.contact_email ? ` · ${customer.contact_email}` : ""}
          </DialogDescription>
        </DialogHeader>

        <CreateWizardSteps steps={MANAGE_STEPS} current={step} onSelect={setStep} testid="manage-customer-wizard-steps" />

        {step === 0 && (
          <div className="space-y-4 py-1" data-testid="manage-customer-wizard-details">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Contact name</Label>
                <Input
                  data-testid="manage-acct-name"
                  value={acct.name}
                  onChange={(e) => setAcct({ ...acct, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Login email</Label>
                <Input
                  data-testid="manage-acct-email"
                  type="email"
                  value={acct.email}
                  onChange={(e) => setAcct({ ...acct, email: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Reset password</Label>
                <Input
                  data-testid="manage-acct-password"
                  type="password"
                  value={acct.password}
                  onChange={(e) => setAcct({ ...acct, password: e.target.value })}
                  placeholder="Leave blank to keep"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Account status</Label>
                <Select value={acct.status} onValueChange={(v) => setAcct({ ...acct, status: v })}>
                  <SelectTrigger data-testid="manage-acct-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>AMD Detection Mode</Label>
              <Select value={amdMode} onValueChange={setAmdMode}>
                <SelectTrigger data-testid="manage-amd-detection-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LAYER1_WITH_CONDITIONAL_LAYER2" data-testid="amd-mode-conditional">
                    Layer 1 + conditional Layer 2 (default)
                  </SelectItem>
                  <SelectItem value="LAYER1_WITH_LAYER2_ALWAYS" data-testid="amd-mode-always">
                    Layer 1 + Layer 2 always
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Conditional runs the AI (Layer 2) detector only when FreeSWITCH (Layer 1) is
                unsure. Always also re-checks confident-human calls; only an explicit voicemail
                phrase can change a human verdict, and it never delays live call handling.
              </p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 py-1" data-testid="manage-customer-wizard-engines">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isReseller ? "Assign Telephony" : "Assign OpenSIPS"}</Label>
                <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto">
                  {opensips.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        data-testid={`manage-opensips-${s.id}`}
                        checked={ops.includes(s.id)}
                        onCheckedChange={(v) => setOps((p) => (v ? [...p, s.id] : p.filter((x) => x !== s.id)))}
                      />
                      {s.name}
                      {s.host ? <span className="text-muted-foreground text-xs">{s.host}</span> : null}
                    </label>
                  ))}
                  {opensips.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Assign Media</Label>
                <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto">
                  {media.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        data-testid={`manage-media-${s.id}`}
                        checked={med.includes(s.id)}
                        onCheckedChange={(v) => setMed((p) => (v ? [...p, s.id] : p.filter((x) => x !== s.id)))}
                      />
                      {s.name}
                      {s.host ? <span className="text-muted-foreground text-xs">{s.host}</span> : null}
                    </label>
                  ))}
                  {media.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>
                Assign AI Engine servers{" "}
                <span className="text-muted-foreground text-xs font-normal">(leave empty = any healthy engine)</span>
              </Label>
              <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto" data-testid="manage-aiengine-list">
                {engines.filter((s) => s.enabled !== false && s.status !== "DISABLED").map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid={`manage-aiengine-${s.id}`}
                      checked={eng.includes(s.id)}
                      onCheckedChange={(v) => setEng((p) => (v ? [...p, s.id] : p.filter((x) => x !== s.id)))}
                    />
                    {s.name}{" "}
                    <span className="text-muted-foreground text-xs">{s.public_ip} · {s.status}</span>
                  </label>
                ))}
                {engines.filter((s) => s.enabled !== false && s.status !== "DISABLED").length === 0 && (
                  <p className="text-xs text-muted-foreground">No active AI engines available.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-1" data-testid="manage-customer-wizard-pricing">
            <div className="space-y-1.5">
              <Label>
                Call-limit pricing{" "}
                <span className="text-muted-foreground text-xs font-normal normal-case">
                  (price per concurrent channel, USD)
                </span>
              </Label>
              <Input
                data-testid="manage-pricing-custom"
                type="number"
                min="0"
                step="0.01"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                placeholder="e.g. 12.00"
              />
              {customer.call_limit_price_minor != null && (
                <p className="text-xs text-muted-foreground">
                  Current stored price: ${(customer.call_limit_price_minor / 100).toFixed(2)}/channel
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="manage-usage-prices">
              <div className="space-y-1.5">
                <Label>
                  AI Model per min{" "}
                  <span className="text-muted-foreground text-xs font-normal normal-case">(USD)</span>
                </Label>
                <Input
                  data-testid="manage-ai-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={usagePrices.ai}
                  onChange={(e) => setUsagePrices((p) => ({ ...p, ai: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  TTS per min{" "}
                  <span className="text-muted-foreground text-xs font-normal normal-case">(USD)</span>
                </Label>
                <Input
                  data-testid="manage-tts-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={usagePrices.tts}
                  onChange={(e) => setUsagePrices((p) => ({ ...p, tts: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  STT per min{" "}
                  <span className="text-muted-foreground text-xs font-normal normal-case">(USD)</span>
                </Label>
                <Input
                  data-testid="manage-stt-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={usagePrices.stt}
                  onChange={(e) => setUsagePrices((p) => ({ ...p, stt: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>
                Call limit{" "}
                <span className="text-muted-foreground text-xs font-normal normal-case">
                  (max concurrent channels; 0 = no calls)
                </span>
              </Label>
              <Input
                data-testid="manage-call-limit"
                type="number"
                min="0"
                step="1"
                value={callLimit}
                onChange={(e) => setCallLimit(e.target.value)}
                placeholder="e.g. 10"
              />
            </div>
          </div>
        )}

        {isAdmin && step === 3 && (
          <div className="space-y-4 py-1" data-testid="manage-customer-wizard-ai">
            <p className="text-sm text-muted-foreground">
              Stored assignment for this customer. It does not change which AI Engine server handles the call,
              and customers cannot edit it.
            </p>
            <div className="space-y-1.5">
              <Label>AI Model</Label>
              <Select value={aiForm.ai_model_id} onValueChange={(v) => setAiForm((f) => ({ ...f, ai_model_id: v }))}>
                <SelectTrigger data-testid="manage-ai-model"><SelectValue placeholder="No model" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No model</SelectItem>
                  {optionRows(catalogs.models, aiForm.ai_model_id).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>TTS</Label>
                <Select value={aiForm.tts_id || undefined} onValueChange={(v) => setAiForm((f) => ({ ...f, tts_id: v }))}>
                  <SelectTrigger data-testid="manage-ai-tts"><SelectValue placeholder="Select TTS" /></SelectTrigger>
                  <SelectContent>
                    {optionRows(catalogs.tts, aiForm.tts_id).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>STT</Label>
                <Select value={aiForm.stt_id || undefined} onValueChange={(v) => setAiForm((f) => ({ ...f, stt_id: v }))}>
                  <SelectTrigger data-testid="manage-ai-stt"><SelectValue placeholder="Select STT" /></SelectTrigger>
                  <SelectContent>
                    {optionRows(catalogs.stt, aiForm.stt_id).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {step > 0 && (
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              onClick={() => setStep((s) => s - 1)}
              data-testid="manage-customer-wizard-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          {!last ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="rounded-lg"
                onClick={finishEarly}
                disabled={busy}
                data-testid="manage-customer-wizard-finish"
              >
                {busy ? "Saving…" : "Finish"}
              </Button>
              <Button
                type="button"
                className="rounded-lg"
                onClick={nextStep}
                data-testid="manage-customer-wizard-next"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <Button
              onClick={saveAll}
              disabled={busy}
              data-testid="manage-saveaccount-btn"
              className="rounded-lg"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
